import React from 'react'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'
import { globalJSONStore } from '#app/features/form/wizard/reducer/createGlobalFormStore.ts'
import { filteredSteps } from '#app/features/form/wizard/utils/filteredSteps.ts'
import { getCombinedSteps } from '#app/features/form/wizard/utils/getCombinedSteps.ts'
import { WizardForm } from '#app/features/form/wizard/wizardForm/index.tsx'

import { type Steps } from '#types/form/wizard.ts'
import { GetStepsSession } from './getSteps.tsx'

export const SessionForm: React.FC<{ data: any }> = ({ data }) => {
	const { t } = useTranslation('intakeTrees')

	const currentLanguage = globalJSONStore(state => state.languageId)
	const includedControllerQuestions = globalJSONStore(
		useShallow(state => state.includedControllerQuestions),
	)
	const questionTree = globalJSONStore(useShallow(state => state.questionTree))
	const answeredQuestionsStack = globalJSONStore(
		useShallow(state => state.answeredQuestionsStack),
	)
	const currentStep = globalJSONStore(state => state.currentStep)
	const currentQuestionId = globalJSONStore(state => state.currentQuestionId)
	const currentQuestionTreeId = globalJSONStore(
		state => state.currentQuestionTreeId,
	)
	const editableParents = globalJSONStore(state => state.editableParents)

	const combinedGetSteps = getCombinedSteps({
		steps: [
			{
				getSteps: GetStepsSession,
				includeContext: true,
				needsController: true,
			},
		],
		contextParams: {
			currentQuestionId,
			currentQuestionTreeId,
			editableParents: editableParents || [],
			questionTree,
			answeredQuestionsStack,
		},
		t,
	})

	const allCurrentSteps = filteredSteps({
		GetSteps: combinedGetSteps,
		includedControllerQuestions: includedControllerQuestions as any,
	})()

	const currentStepProps = allCurrentSteps[currentStep]
	const isLastStep = allCurrentSteps.length === currentStep + 1

	const formText = {
		title: t('form.title'),
		subtitle: t('form.subtitle'),
		finishedText: t('form.finishedText'),
		finishedSubtitle: t('form.finishedSubtitle'),
		finishedButtonText: t('form.finishedButtonText'),
	}

	return (
		<WizardForm
			actionRoute={`/${currentLanguage}/session`}
			translationStrings={['introductionForm']}
			data={data}
			useGlobalFormStore={globalJSONStore}
			getSteps={filteredSteps({
				GetSteps: combinedGetSteps,
				includedControllerQuestions: includedControllerQuestions || [],
			})}
			currentSteps={
				filteredSteps({
					GetSteps: combinedGetSteps,
					includedControllerQuestions: includedControllerQuestions || [],
				})()[currentStep] as Steps<any>
			}
			currentStepProps={{
				id: currentStepProps?.id,
				fieldKey: currentStepProps?.fieldKey,
				isInJsonTree: currentStepProps?.isInJsonTree,
				questionText: currentStepProps?.questionText,
				validator: currentStepProps?.validator,
				component: currentStepProps?.component,
				hasBackendCall: currentStepProps?.hasBackendCall,
				backendAction: currentStepProps?.backendAction,
				formHasBeenCompleted: data?.serverData?.formHasBeenCompleted ?? false,
				isLastStep,
			}}
			formText={formText}
		/>
	)
}
